import {AxiosError} from "axios";
import {useEffect, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';
import Modal from "react-bootstrap/Modal";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ApiConfig} from "../api/portal/ApiConfig";
import {AuthApi, PortalSignupValidationResult} from "../api/portal/generated";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {XSRF} from "./Xsrf";

/*
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());

export const PortalSignup = () =>
{
    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded signup validation code
     */
    const [signupCode, setSignupCode] = useState<string>();
    /**
     * The result of the validation code
     */
    const [signupValidation, setSignupValidation] = useState<PortalSignupValidationResult>({email: ""});
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The password for the user
     */
    const [password, setPassword] = useState<string>();
    /**
     * The repeated password for the user
     */
    const [passwordRepeat, setPasswordRepeat] = useState<string>();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation("components");

    useEffect(() =>
    {
        const dParameter = searchParams.get("d");

        if (dParameter === null)
        {
            logger.warn("signup URL is missing the d parameter");

            setErrorMessage(t('portal-signup.reset-data-missing'));

            return;
        }

        authApi.authControllerValidatePortalSignup(dParameter)
            .then(response =>
            {
                setSignupCode(dParameter);

                setSignupValidation(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating signup link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
            })
    }, [searchParams, t]);

    const signup = () =>
    {
        if (signupCode === undefined) {
            throw Error("signupCode is undefined");
        }

        if (password === undefined) {
            throw Error("password is undefined");
        }

        if (password !== passwordRepeat)
        {
            setErrorMessage(t('portal-signup.password-mismatch'));
            return;
        }

        // a XSRF cookie is required
        XSRF.generateCookie();

        authApi.authControllerSetPortalSignupPassword(signupCode, password)
            .then(() =>
            {
                navigate("/");
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating signup link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    return (
        <Modal show={true} centered={true} animation={false}>
            <Modal.Header>
                <Modal.Title className="w-100">{t('portal-signup.title')}</Modal.Title>
                <img src={Utils.createPublicHostResourceUrl("login-logo")} width="150" alt="CipherMail Logo"
                     className="float-right"/>
            </Modal.Header>
            <Modal.Body>

                {/* show an alert if a validation error occurred */}
                <RenderAlert message={errorMessage}/>

                <FloatingLabel label={t('portal-signup.email-label')} className="text-secondary mb-3">
                    <Form.Control type="email" value={signupValidation.email} placeholder="Email"
                                  disabled={true}/>
                </FloatingLabel>

                <FloatingLabel label={t('portal-signup.password-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={password} placeholder="Password"
                                  onChange={e => setPassword(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t('portal-signup.password-repeat-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={passwordRepeat} placeholder="Password Repeat"
                                  onChange={e => setPasswordRepeat(e.target.value)}/>
                </FloatingLabel>
                {t('portal-signup.text')}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" disabled={StringUtils.isEmpty(signupValidation.email) || StringUtils.isEmpty(password)}
                        onClick={signup}>
                    {t('portal-signup.signup-button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
